import L from 'leaflet';
import { BLOCK_SHIFT, blockFromKey, blockKey, cellIndex, chunkFromKey } from './coords';
import type { ChunkGrid } from './chunkGrid';

// ============================================================
// Couches canvas de la carte (L.CRS.Simple, 1 unité = 1 chunk)
// ============================================================
// À zoom Z, la tuile (tx, ty) de 256 px couvre S = 256/2^Z chunks de côté à partir de (tx*S, ty*S).
// Chaque tuile est peinte à 1 pixel par chunk dans un ImageData, puis agrandie sans lissage : le
// coût ne dépend que de la surface de la tuile, jamais du nombre de chunks du monde. Pour Z < 0,
// on échantillonne un chunk sur 2 ou 4.
// ============================================================

/** Rendu d'une instance : quel groupe pour chaque idx, et quelle couleur pour chaque groupe. */
export interface ChunkPaint {
  /** idx du pays → groupe (0 = transparent). Longueur 65536. */
  group: Uint16Array;
  /** groupe → pixel RGBA empaqueté (octets r, g, b, a dans l'ordre mémoire d'ImageData). */
  fill: Uint32Array;
  /**
   * Frontières entre groupes : une couleur unique (pays) ou une par groupe (régions). `minZoom` :
   * premier zoom où elles sont tracées (défaut 2, soit 4 px par chunk ; 0 pour les régions, tracées
   * à tous les zooms utiles comme chez NG).
   */
  stroke: null | ({ width: number; minZoom?: number } & ({ color: string } | { byGroup: string[] }));
}

/** Premier zoom des frontières quand `stroke.minZoom` est absent. */
const STROKE_MIN_ZOOM = 2;

/** Pixel d'ImageData (little-endian) : a en poids fort. */
export function packRgba(r: number, g: number, b: number, a: number): number {
  return ((a & 255) << 24 | (b & 255) << 16 | (g & 255) << 8 | (r & 255)) >>> 0;
}

/**
 * Zooms de la carte (voir useWorldMap). GridLayer a minZoom = 0 par défaut : sans ces bornes, les
 * couches disparaîtraient en vue large (zooms -1 et -2).
 */
const ZOOMS = { minZoom: -2, maxZoom: 8 };

function dpr(): number {
  return Math.min(2, window.devicePixelRatio || 1);
}

/** Chunks par côté de tuile au zoom z. */
function span(z: number): number {
  return z >= 0 ? 256 >> z : 256 << -z;
}

export class ChunkGridLayer extends L.GridLayer {
  private chunkGrid: ChunkGrid | null = null;
  private chunkPaint: ChunkPaint;
  private bordersOn = true;
  private readonly scratch = document.createElement('canvas');
  private readonly images = new Map<number, ImageData>();

  constructor(paint: ChunkPaint, options?: L.GridLayerOptions) {
    super({ tileSize: 256, keepBuffer: 2, updateWhenZooming: false, ...ZOOMS, ...options });
    this.chunkPaint = paint;
  }

  setGrid(grid: ChunkGrid | null): void {
    this.chunkGrid = grid;
    this.repaintAll();
  }

  setPaint(paint: ChunkPaint): void {
    this.chunkPaint = paint;
    this.repaintAll();
  }

  setBorders(on: boolean): void {
    if (on === this.bordersOn) return;
    this.bordersOn = on;
    this.repaintAll();
  }

  /** Redessine les seules tuiles qui recoupent un bloc modifié (marge d'une cellule). */
  repaintBlocks(keys: number[]): void {
    if (!this._map) return;
    if (keys.length > 256) { this.repaintAll(); return; }
    const rects = keys.map((k) => {
      const { bx, bz } = blockFromKey(k);
      return [(bx << BLOCK_SHIFT) - 1, (bx << BLOCK_SHIFT) + 32, (bz << BLOCK_SHIFT) - 1, (bz << BLOCK_SHIFT) + 32];
    });
    for (const key in this._tiles) {
      const t = this._tiles[key];
      const S = span(t.coords.z);
      const x0 = t.coords.x * S;
      const z0 = t.coords.y * S;
      if (rects.some((r) => r[0] <= x0 + S - 1 && r[1] >= x0 && r[2] <= z0 + S - 1 && r[3] >= z0)) {
        this.drawTile(t.el as HTMLCanvasElement, t.coords);
      }
    }
  }

  /** Redessin en place de toutes les tuiles (couleur, activation, grille remplacée). */
  repaintAll(): void {
    if (!this._map) return;
    for (const key in this._tiles) {
      const t = this._tiles[key];
      this.drawTile(t.el as HTMLCanvasElement, t.coords);
    }
  }

  protected createTile(coords: L.Coords): HTMLElement {
    const tile = document.createElement('canvas');
    this.drawTile(tile, coords);
    return tile;
  }

  private imageFor(size: number): ImageData {
    let img = this.images.get(size);
    if (!img) { img = new ImageData(size, size); this.images.set(size, img); }
    return img;
  }

  private drawTile(canvas: HTMLCanvasElement, coords: L.Coords): void {
    const W = Math.round(256 * dpr());
    if (canvas.width !== W) { canvas.width = W; canvas.height = W; }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, W);
    const grid = this.chunkGrid;
    if (!grid || grid.blocks.size === 0) return;

    const { group, fill, stroke } = this.chunkPaint;
    const S = span(coords.z);
    const x0 = coords.x * S;
    const z0 = coords.y * S;
    const step = S > 256 ? S / 256 : 1;
    const out = S / step;

    // Blocs recoupant la tuile, plus une cellule de marge pour les frontières du bord.
    const bx0 = (x0 - 1) >> BLOCK_SHIFT;
    const bx1 = (x0 + S) >> BLOCK_SHIFT;
    const bz0 = (z0 - 1) >> BLOCK_SHIFT;
    const bz1 = (z0 + S) >> BLOCK_SHIFT;
    let any = false;
    const img = this.imageFor(out);
    const px = new Uint32Array(img.data.buffer);
    px.fill(0);
    for (let bz = bz0; bz <= bz1; bz++) {
      for (let bx = bx0; bx <= bx1; bx++) {
        const cells = grid.getBlock(bx, bz);
        if (!cells) continue;
        any = true;
        const cbx = bx << BLOCK_SHIFT;
        const cbz = bz << BLOCK_SHIFT;
        const lx0 = Math.max(0, x0 - cbx);
        const lx1 = Math.min(31, x0 + S - 1 - cbx);
        const lz0 = Math.max(0, z0 - cbz);
        const lz1 = Math.min(31, z0 + S - 1 - cbz);
        for (let lz = lz0; lz <= lz1; lz++) {
          const dz = cbz + lz - z0;
          if (dz % step !== 0) continue;
          const row = (dz / step) * out;
          for (let lx = lx0; lx <= lx1; lx++) {
            const dx = cbx + lx - x0;
            if (dx % step !== 0) continue;
            const g = group[cells[(lz << BLOCK_SHIFT) | lx]];
            if (g) px[row + dx / step] = fill[g];
          }
        }
      }
    }
    if (!any) return;

    this.scratch.width = out;
    this.scratch.height = out;
    const sctx = this.scratch.getContext('2d');
    if (!sctx) return;
    sctx.putImageData(img, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.scratch, 0, 0, out, out, 0, 0, W, W);

    // Frontières : à partir de stroke.minZoom, voisins lus dans la grille globale (les arêtes du
    // bord de tuile comparent avec la tuile voisine). Sous le zoom 0 (moins d'un pixel par chunk),
    // jamais : S x S lectures par tuile deviendraient trop coûteuses.
    if (!stroke || !this.bordersOn || coords.z < Math.max(0, stroke.minZoom ?? STROKE_MIN_ZOOM)) return;
    const p = W / S;
    // Dernier bloc lu gardé sous la main : g, r et b tombent presque toujours dans le même bloc,
    // ce qui évite une recherche dans la Map par cellule (65 536 cellules par tuile au zoom 0).
    let lastKey = NaN;
    let lastCells: Uint16Array | undefined;
    const gAt = (cx: number, cz: number) => {
      const k = blockKey(cx >> BLOCK_SHIFT, cz >> BLOCK_SHIFT);
      if (k !== lastKey) { lastKey = k; lastCells = grid.blocks.get(k); }
      return lastCells ? group[lastCells[cellIndex(cx, cz)]] : 0;
    };
    const single = 'color' in stroke;
    const paths = new Map<number, number[]>();
    const seg = (g: number, xa: number, ya: number, xb: number, yb: number) => {
      const key = single ? 0 : g;
      let arr = paths.get(key);
      if (!arr) { arr = []; paths.set(key, arr); }
      arr.push(xa, ya, xb, yb);
    };
    for (let dz = 0; dz < S; dz++) {
      const cz = z0 + dz;
      for (let dx = 0; dx < S; dx++) {
        const cx = x0 + dx;
        const g = gAt(cx, cz);
        const r = gAt(cx + 1, cz);
        if (g !== r) {
          const x = (dx + 1) * p;
          if (g) seg(g, x, dz * p, x, (dz + 1) * p);
          if (r && !single) seg(r, x, dz * p, x, (dz + 1) * p);
          else if (r && !g) seg(r, x, dz * p, x, (dz + 1) * p);
        }
        const b = gAt(cx, cz + 1);
        if (g !== b) {
          const y = (dz + 1) * p;
          if (g) seg(g, dx * p, y, (dx + 1) * p, y);
          if (b && !single) seg(b, dx * p, y, (dx + 1) * p, y);
          else if (b && !g) seg(b, dx * p, y, (dx + 1) * p, y);
        }
        if (dx === 0) {
          const l = gAt(cx - 1, cz);
          if (g !== l && (g || l)) seg(g || l, 0, dz * p, 0, (dz + 1) * p);
        }
        if (dz === 0) {
          const t = gAt(cx, cz - 1);
          if (g !== t && (g || t)) seg(g || t, dx * p, 0, (dx + 1) * p, 0);
        }
      }
    }
    ctx.lineWidth = stroke.width * dpr();
    ctx.lineCap = 'square';
    for (const [g, arr] of paths) {
      ctx.strokeStyle = 'color' in stroke ? stroke.color : (stroke.byGroup[g] ?? '#000');
      ctx.beginPath();
      for (let i = 0; i < arr.length; i += 4) {
        ctx.moveTo(arr[i], arr[i + 1]);
        ctx.lineTo(arr[i + 2], arr[i + 3]);
      }
      ctx.stroke();
    }
  }
}

/**
 * Quadrillage. Variante `base` : fond neutre d'un niveau sans tuiles (blocs de 32 chunks, chunks à
 * partir de 16 px par chunk, axes x = 0 et z = 0 plus marqués), traits clairs sur le fond sombre.
 * Variante `overlay` : couche « Grille des chunks », posée au-dessus des tuiles et des chunks peints
 * (comme le trait par chunk de PaintedChunksLayer chez NG) : chunks dès 4 px par chunk, traits
 * sombres doublés d'un liseré clair, lisibles sur le terrain comme sur les couleurs des pays.
 */
export class GridLinesLayer extends L.GridLayer {
  private readonly variant: 'base' | 'overlay';

  constructor(options?: L.GridLayerOptions & { variant?: 'base' | 'overlay' }) {
    const { variant = 'base', ...rest } = options ?? {};
    super({ tileSize: 256, keepBuffer: 1, ...ZOOMS, ...rest });
    this.variant = variant;
  }

  protected createTile(coords: L.Coords): HTMLElement {
    const tile = document.createElement('canvas');
    const W = Math.round(256 * dpr());
    tile.width = W;
    tile.height = W;
    const ctx = tile.getContext('2d');
    if (!ctx) return tile;
    const S = span(coords.z);
    const x0 = coords.x * S;
    const z0 = coords.y * S;
    const p = W / S;
    /** Traits tous les `every` chunks ; `shift` décale d'un pixel (liseré clair d'un trait sombre). */
    const lines = (every: number, color: string, width: number, shift = 0) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = width * dpr();
      ctx.beginPath();
      const first = (v: number) => Math.ceil(v / every) * every;
      const d = shift * dpr();
      for (let x = first(x0); x < x0 + S; x += every) { const X = (x - x0) * p + d; ctx.moveTo(X, 0); ctx.lineTo(X, W); }
      for (let z = first(z0); z < z0 + S; z += every) { const Y = (z - z0) * p + d; ctx.moveTo(0, Y); ctx.lineTo(W, Y); }
      ctx.stroke();
    };
    const cssPerChunk = coords.z >= 0 ? 1 << coords.z : 1 / (1 << -coords.z);
    if (this.variant === 'overlay') {
      // Trait sombre et liseré clair décalé d'un pixel : visible sur le terrain clair comme sur
      // l'océan ou un pays sombre. Sous 8 px par chunk, le trait sombre seul (moins chargé).
      if (cssPerChunk >= 4) lines(1, 'rgba(0,0,0,0.30)', 1);
      if (cssPerChunk >= 8) lines(1, 'rgba(255,255,255,0.16)', 1, 1);
      lines(32, 'rgba(0,0,0,0.50)', 1);
      lines(32, 'rgba(255,255,255,0.22)', 1, 1);
      return tile;
    }
    if (cssPerChunk >= 16) lines(1, 'rgba(255,255,255,0.05)', 1);
    lines(32, 'rgba(255,255,255,0.10)', 1);
    // Axes : z = 0 et x = 0.
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = dpr();
    ctx.beginPath();
    if (x0 <= 0 && 0 < x0 + S) { const X = (0 - x0) * p; ctx.moveTo(X, 0); ctx.lineTo(X, W); }
    if (z0 <= 0 && 0 < z0 + S) { const Y = (0 - z0) * p; ctx.moveTo(0, Y); ctx.lineTo(W, Y); }
    ctx.stroke();
    return tile;
  }
}

/**
 * Aperçu d'un trait de pinceau : un canvas de la taille de la carte, complété au fil du geste
 * (seules les nouvelles cellules sont dessinées) et redessiné en entier après un déplacement.
 * Dès 4 px par chunk, chaque chunk reçoit un bord blanc de 1,5 px (BrushPreviewLayer de NG) : on
 * voit exactement quels chunks partiront au serveur.
 */
export class CellPreviewLayer extends L.Layer {
  private canvas: HTMLCanvasElement | null = null;
  private readonly cells = new Set<number>();
  private color = '#FFFFFF';

  onAdd(map: L.Map): this {
    const canvas = L.DomUtil.create('canvas', 'worlds-cell-preview');
    canvas.style.position = 'absolute';
    canvas.style.pointerEvents = 'none';
    canvas.style.opacity = '0.5';
    map.getPane(this.options.pane ?? 'overlayPane')?.appendChild(canvas);
    this.canvas = canvas;
    map.on('moveend zoomend resize viewreset', this.resetCanvas, this);
    map.on('zoomstart', this.hideCanvas, this);
    this.resetCanvas();
    return this;
  }

  onRemove(map: L.Map): this {
    map.off('moveend zoomend resize viewreset', this.resetCanvas, this);
    map.off('zoomstart', this.hideCanvas, this);
    this.canvas?.remove();
    this.canvas = null;
    return this;
  }

  setColor(color: string): void {
    this.color = color;
  }

  has(key: number): boolean {
    return this.cells.has(key);
  }

  get size(): number {
    return this.cells.size;
  }

  keys(): IterableIterator<number> {
    return this.cells.values();
  }

  add(keys: number[]): void {
    const fresh: number[] = [];
    for (const k of keys) if (!this.cells.has(k)) { this.cells.add(k); fresh.push(k); }
    this.drawCells(fresh);
  }

  clear(): void {
    this.cells.clear();
    this.resetCanvas();
  }

  private hideCanvas(): void {
    if (this.canvas) this.canvas.style.visibility = 'hidden';
  }

  private resetCanvas(): void {
    const map = this._map;
    const canvas = this.canvas;
    if (!map || !canvas) return;
    const size = map.getSize();
    const r = dpr();
    canvas.width = Math.round(size.x * r);
    canvas.height = Math.round(size.y * r);
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;
    canvas.style.visibility = 'visible';
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    this.drawCells(this.cells.values());
  }

  private drawCells(keys: Iterable<number>): void {
    const map = this._map;
    const canvas = this.canvas;
    if (!map || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const r = dpr();
    const origin = map.latLngToContainerPoint([0, 0]);
    const scale = Math.pow(2, map.getZoom());
    const w = canvas.width;
    const h = canvas.height;
    const side = Math.max(1, scale * r);
    const outline = scale >= 4;
    ctx.fillStyle = this.color;
    if (outline) {
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 1.5 * r;
    }
    for (const k of keys) {
      const { cx, cz } = chunkFromKey(k);
      const x = (origin.x + cx * scale) * r;
      const y = (origin.y + cz * scale) * r;
      if (x + side < 0 || y + side < 0 || x > w || y > h) continue;
      ctx.fillRect(x, y, side, side);
      // Le bord est tracé après le remplissage : un chunk ajouté plus tard recouvre la moitié
      // intérieure du bord voisin, puis retrace le sien sur la même arête.
      if (outline) ctx.strokeRect(x, y, side, side);
    }
  }
}
